import React, { useState, useEffect, useRef } from "react";
import moment from "moment";

import { IDog, INoseworkProgress, INoseworkLevelProgress, IDogRunSummary, ISportProgress } from "@eagerdog/interfaces";
import { Constants } from "@eagerdog/constants";

import Button from "../../../components/Button/Button";
import { Modal, useModal } from "../../../components/Modal/Modal";
import { ManualRunEntryModal } from "../ManualRunEntry";
import { apiService } from "../../../services/api.service";
import { toast } from "../../../components/Toast/ToastManager";
import ContextMenu, { IContextMenuOption } from "../../../components/ContextMenu/ContextMenu";

import { AgGridReact } from "ag-grid-react";
import { ColDef, themeQuartz } from "ag-grid-community";

import "./NoseworkCard.scss";

interface IProps {
  dog: IDog;
  noseworkData: INoseworkProgress;
  onRunUpdated?: () => void;
}

const NoseworkCard: React.FC<IProps> = ({ dog, noseworkData, onRunUpdated }) => {
  // State for viewing runs modal
  const [showRunsModal, setShowRunsModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingRun, setEditingRun] = useState<IDogRunSummary | undefined>(undefined);
  const [deletingRunId, setDeletingRunId] = useState<string | null>(null);
  const [runToDelete, setRunToDelete] = useState<string | null>(null);
  const [localRuns, setLocalRuns] = useState<IDogRunSummary[]>(noseworkData.recent_runs || []);
  const deleteConfirmModal = useModal();
  
  // Champion runs modal state
  const [showChampionRunsModal, setShowChampionRunsModal] = useState(false);
  const [championModalData, setChampionModalData] = useState<{
    level: string;
    titleType: 'champion' | 'grand';
    titleName: string;
    runs: Array<IDogRunSummary & { indicator: 'counting' | 'extra' | 'non-qualifying' }>;
    progress: number;
    required: number;
    elementBreakdown?: Array<{
      element: string;
      elementName: string;
      count: number;
      required: number;
    }>;
  } | null>(null);
  
  // All titles modal state
  const [showAllTitlesModal, setShowAllTitlesModal] = useState(false);
  
  // Track if we need to refresh parent when modal closes
  const needsParentRefreshRef = useRef(false);
  

  // Sync localRuns with noseworkData when it changes
  useEffect(() => {
    setLocalRuns(noseworkData.recent_runs || []);
  }, [noseworkData.recent_runs]);

  // Handle clicking on element title to view runs
  const handleShowElementRuns = (level: INoseworkLevelProgress, elementName: string) => {
    const element = level.elements.find(e => e.element === elementName);
    if (!element) return;

    const allRuns = noseworkData.recent_runs || [];
    
    // Get all runs for this element at this level
    let elementRuns = allRuns.filter(run => 
      run.level === level.level &&
      run.element === elementName
    );

    // Sort by date
    elementRuns.sort((a, b) => new Date(a.trial_date).getTime() - new Date(b.trial_date).getTime());

    // Indicators come from the server's counting_run_ids (shared with mobile)
    const countingIds = new Set(element.counting_run_ids || []);
    const runsWithIndicators = elementRuns.map(run => {
      const isQualified = run.result_status === Constants.run_result_status.qualified;
      if (isQualified && countingIds.has(run._id)) {
        return { ...run, indicator: 'counting' as const };
      } else if (isQualified) {
        return { ...run, indicator: 'extra' as const };
      } else {
        return { ...run, indicator: 'non-qualifying' as const };
      }
    });

    setChampionModalData({
      level: level.level,
      titleType: 'champion',
      titleName: `${level.level} ${elementName}`,
      runs: runsWithIndicators,
      progress: element.total_qs,
      required: element.required_qs
    });
    setShowChampionRunsModal(true);
  };

  // Handle clicking on level title to view all runs for that level
  const handleShowLevelRuns = (level: INoseworkLevelProgress) => {
    const allRuns = noseworkData.recent_runs || [];
    
    // Get all runs for this level (excluding Handler Disc for level title)
    const baseElements = [
      Constants.dog_class_element.nosework_interior,
      Constants.dog_class_element.nosework_exterior,
      Constants.dog_class_element.nosework_container,
      Constants.dog_class_element.nosework_vehicle
    ];

    let levelRuns = allRuns.filter(run => 
      run.level === level.level &&
      baseElements.includes(run.element || '')
    );

    // Sort by date
    levelRuns.sort((a, b) => new Date(a.trial_date).getTime() - new Date(b.trial_date).getTime());

    // Indicators come from each element's counting_run_ids (server-computed)
    const levelCountingIds = new Set<string>();
    level.elements.forEach(e => (e.counting_run_ids || []).forEach(id => levelCountingIds.add(id)));

    const runsWithIndicators = levelRuns.map(run => {
      const isQualified = run.result_status === Constants.run_result_status.qualified;
      if (isQualified && levelCountingIds.has(run._id)) {
        return { ...run, indicator: 'counting' as const };
      } else if (isQualified) {
        return { ...run, indicator: 'extra' as const };
      } else {
        return { ...run, indicator: 'non-qualifying' as const };
      }
    });

    setChampionModalData({
      level: level.level,
      titleType: 'champion',
      titleName: `${level.level} Level Title (${level.level_title_code || ''})`,
      runs: runsWithIndicators,
      progress: level.elements.filter(e => e.title_earned && e.element !== Constants.dog_class_element.nosework_handler_descrimination).length,
      required: 4 // 4 base elements
    });
    setShowChampionRunsModal(true);
  };

  // Handle clicking on champion/grand progress to view contributing runs
  const handleShowChampionRuns = (level: INoseworkLevelProgress, titleType: 'champion' | 'grand') => {
    const allRuns = noseworkData.recent_runs || [];
    
    // Filter for B-division runs at this level, after level title was earned
    const levelTitleDate = level.level_title_earned_date;
    if (!levelTitleDate) return;
    
    let bRunsForLevel = allRuns.filter(run => 
      run.level === level.level &&
      run.division === 'B' &&
      new Date(run.trial_date) >= new Date(levelTitleDate)
    );
    
    // For grand champion, further filter to runs after champion was earned
    if (titleType === 'grand' && level.class_champion_earned_date) {
      bRunsForLevel = bRunsForLevel.filter(run =>
        new Date(run.trial_date) >= new Date(level.class_champion_earned_date!)
      );
    }

    // Only include 4 base elements (not Handler Disc)
    const baseElements = [
      Constants.dog_class_element.nosework_interior,
      Constants.dog_class_element.nosework_exterior,
      Constants.dog_class_element.nosework_container,
      Constants.dog_class_element.nosework_vehicle
    ];

    bRunsForLevel = bRunsForLevel.filter(run => baseElements.includes(run.element || ''));
    
    // Sort by date
    bRunsForLevel.sort((a, b) => new Date(a.trial_date).getTime() - new Date(b.trial_date).getTime());
    
    // Counting runs per element come from the server (champion_/grand_counting_run_ids)
    const maxPerElement = titleType === 'champion' ? 3 : 5;
    const elementProgress = (titleType === 'champion'
      ? level.class_champion_element_progress
      : level.class_grand_champion_element_progress) || [];
    const countingIds = new Set<string>();
    const elementCounts: Record<string, number> = {};
    baseElements.forEach(el => {
      const ep = elementProgress.find(p => p.element === el);
      const ids = (titleType === 'champion' ? ep?.champion_counting_run_ids : ep?.grand_counting_run_ids) || [];
      ids.forEach(id => countingIds.add(id));
      elementCounts[el] = ids.length;
    });

    const runsWithIndicators = bRunsForLevel.map(run => {
      const isQualified = run.result_status === Constants.run_result_status.qualified;
      if (isQualified && countingIds.has(run._id)) {
        return { ...run, indicator: 'counting' as const };
      } else if (isQualified) {
        return { ...run, indicator: 'extra' as const };
      } else {
        return { ...run, indicator: 'non-qualifying' as const };
      }
    });

    // Build element breakdown for display
    const elementNameMap: Record<string, string> = {
      [Constants.dog_class_element.nosework_interior]: 'Interior',
      [Constants.dog_class_element.nosework_exterior]: 'Exterior',
      [Constants.dog_class_element.nosework_container]: 'Container',
      [Constants.dog_class_element.nosework_vehicle]: 'Vehicle'
    };

    const elementBreakdown = baseElements.map(element => ({
      element,
      elementName: elementNameMap[element],
      count: elementCounts[element] || 0,
      required: maxPerElement
    }));

    // Generate title abbreviation based on level if not already earned
    const getChampionAbbreviation = (levelName: string, type: 'champion' | 'grand') => {
      const levelMap: Record<string, string> = {
        'Novice': 'N',
        'Advanced': 'A',
        'Superior': 'S',
        'Master': 'M',
        'Elite': 'E'
      };
      
      const levelPrefix = levelMap[levelName] || levelName.charAt(0).toUpperCase();
      
      if (type === 'champion') {
        return `${levelPrefix}${levelPrefix}CH`; // e.g., NNCH, ACH, SCH, MCH, ECH
      } else {
        return `${levelPrefix}GC`; // e.g., NGC, AGC, SGC, MGC, EGC
      }
    };

    const championCode = level.class_champion_code || getChampionAbbreviation(level.level, 'champion');
    const grandCode = level.class_grand_champion_code || getChampionAbbreviation(level.level, 'grand');

    const titleName = titleType === 'champion' 
      ? `${level.level} Class Champion (${championCode})` 
      : `${level.level} Grand Champion (${grandCode})`;

    setChampionModalData({
      level: level.level,
      titleType,
      titleName,
      runs: runsWithIndicators,
      progress: titleType === 'champion' ? (level.class_champion_progress || 0) : (level.class_grand_champion_progress || 0),
      required: titleType === 'champion' ? 12 : 20,
      elementBreakdown
    });
    setShowChampionRunsModal(true);
  };

  // Render title badges (earned titles)
  const renderTitleBadge = (title: { title_code: string; title_name: string; earned_date: Date; color_hex?: string }) => {
    return (
      <div 
        key={title.title_code} 
        className="titleBadge"
        style={{ backgroundColor: title.color_hex || '#4CAF50' }}
      >
        <div className="badgeInfo">
          <div className="badgeTitle">{title.title_code}</div>
          <div className="badgeDate">{moment(title.earned_date).format('M/D/YY')}</div>
        </div>
      </div>
    );
  };

  // Render element progress cell
  const renderElementCell = (level: INoseworkLevelProgress, elementName: string) => {
    const element = level.elements.find(e => e.element === elementName);
    if (!element) return null;

    const isComplete = element.title_earned;
    const progress = `${element.total_qs}/${element.required_qs}`;

    return (
      <div 
        key={`${level.level}-${elementName}`}
        className={`elementCell ${isComplete ? 'complete' : ''} clickable`}
        title={`${level.level} ${elementName}: ${progress} Qs - Click to view runs`}
        onClick={() => handleShowElementRuns(level, elementName)}
      >
        {isComplete ? (
          <div className="titleCode">{element.title_code}</div>
        ) : (
          <div className="progress">{progress}</div>
        )}
      </div>
    );
  };

  // Render level row in the grid
  const renderLevelRow = (level: INoseworkLevelProgress) => {
    return (
      <div key={level.level} className="levelRow">
        <div className="levelLabel">{level.level}</div>
        {[
          Constants.dog_class_element.nosework_interior,
          Constants.dog_class_element.nosework_exterior,
          Constants.dog_class_element.nosework_container,
          Constants.dog_class_element.nosework_vehicle,
          Constants.dog_class_element.nosework_handler_descrimination
        ].map(element => 
          renderElementCell(level, element)
        )}
        <div className="levelTitleCell">
          {level.level_title_earned ? (
            <span 
              className="levelTitle clickable" 
              title={`${level.level_title_name} - Click to view runs`}
              onClick={() => handleShowLevelRuns(level)}
            >
              {level.level_title_code}
            </span>
          ) : (
            <span className="incomplete">-</span>
          )}
        </div>
        <div className="championCell">
          {level.class_champion_earned ? (
            <span 
              className="champion clickable" 
              title={`${level.class_champion_name} - Click to view runs`}
              onClick={() => handleShowChampionRuns(level, 'champion')}
            >
              {level.class_champion_code}
            </span>
          ) : level.level_title_earned ? (
            <span 
              className="championProgress clickable" 
              onClick={() => handleShowChampionRuns(level, 'champion')}
              title="Click to view contributing runs"
            >
              {level.class_champion_progress || 0}/{level.class_champion_required || 12}
            </span>
          ) : (
            <span className="locked">🔒</span>
          )}
        </div>
        <div className="grandChampionCell">
          {level.class_grand_champion_earned ? (
            <span 
              className="grandChampion clickable" 
              title={`${level.class_grand_champion_name} - Click to view runs`}
              onClick={() => handleShowChampionRuns(level, 'grand')}
            >
              {level.class_grand_champion_code}
            </span>
          ) : level.class_champion_earned ? (
            <span 
              className="grandChampionProgress clickable"
              onClick={() => handleShowChampionRuns(level, 'grand')}
              title="Click to view contributing runs"
            >
              {level.class_grand_champion_progress || 0}/{level.class_grand_champion_required || 20}
            </span>
          ) : (
            <span className="locked">🔒</span>
          )}
        </div>
      </div>
    );
  };

  // Render a single run row in the modal - UNUSED (converted to AGGrid)
  /*
  const renderRunRow = (run: IDogRunSummary) => {
    const isManualEntry = !run.event;
    const isDeleting = deletingRunId === run._id;

    const menuOptions: IContextMenuOption[] = [
      {
        label: 'Edit',
        onClick: () => handleEditRun(run)
      },
      {
        label: 'Delete',
        onClick: () => handleDeleteRun(run._id)
      }
    ];

    const resultClass = run.result_status === 'Q' ? 'qualified' : 'not-qualified';

    return (
      <tr key={run._id} className={isDeleting ? 'deleting' : ''}>
        <td>{moment(run.trial_date).format('M/D/YYYY')}</td>
        <td>{run.element || '-'}</td>
        <td>{run.level || '-'}</td>
        <td>{run.division || '-'}</td>
        <td className={`result ${resultClass}`}>
          {run.result_status || '-'}
        </td>
        <td className="hideOnMobile">
          {run.event?.name || 'Manual Entry'}
        </td>
        <td className="actions">
          {run.verified && (
            <div className="verifiedBadge">
              <img src={PawIcon} alt="Verified" className="verifiedIcon" />
              <span className="verifiedText">Verified</span>
            </div>
          )}
          {isManualEntry && !isDeleting && (
            <ContextMenu options={menuOptions} />
          )}
          {isDeleting && <span className="deletingText">Deleting...</span>}
        </td>
      </tr>
    );
  };
  */

  const handleAddManualRun = () => {
    setEditingRun(undefined);
    setIsModalOpen(true);
  };

  const handleEditRun = (run: IDogRunSummary) => {
    setEditingRun(run);
    setIsModalOpen(true);
  };

  const handleDeleteRun = (runId: string) => {
    setRunToDelete(runId);
    deleteConfirmModal.toggle();
  };

  // Stable refs for AG Grid cell renderer callbacks (avoids stale closure issues)
  const handleEditRunRef = useRef(handleEditRun);
  const handleDeleteRunRef = useRef(handleDeleteRun);
  const deletingRunIdRef = useRef(deletingRunId);
  useEffect(() => { handleEditRunRef.current = handleEditRun; });
  useEffect(() => { handleDeleteRunRef.current = handleDeleteRun; });
  useEffect(() => { deletingRunIdRef.current = deletingRunId; }, [deletingRunId]);

  const confirmDelete = async () => {
    if (!runToDelete) return;

    setDeletingRunId(runToDelete);
    deleteConfirmModal.toggle();
    
    try {
      await apiService.deleteManualRun(runToDelete);
      toast.show({
        title: "Run Deleted",
        content: "The run has been removed and title progress has been recalculated.",
        duration: 5000,
        type: "success"
      });
      
      // Refresh the local runs list
      const response = await apiService.getRibbonRoomData(dog._id);
      const noseworkSport = response.sports.find((s: ISportProgress) => s.show_type === 'Nosework');
      if (noseworkSport) {
        setLocalRuns(noseworkSport.recent_runs || []);
      }
      
      // Mark that parent needs refresh when modal closes
      needsParentRefreshRef.current = true;
    } catch (error: any) {
      console.error('Error deleting run:', error);
      toast.show({
        title: "Delete Failed",
        content: "Unable to delete the run. Please try again.",
        duration: 10000,
        type: "fail",
        errorDetails: error
      });
    } finally {
      setDeletingRunId(null);
      setRunToDelete(null);
    }
  };

  const handleModalSuccess = async () => {
    setIsModalOpen(false);
    setEditingRun(undefined);
    
    // Fetch fresh run data to update the table without closing the modal
    try {
      const response = await apiService.getRibbonRoomData(dog._id);
      const noseworkSport = response.sports.find((s: ISportProgress) => s.show_type === 'Nosework');
      if (noseworkSport) {
        setLocalRuns(noseworkSport.recent_runs || []);
      }
    } catch (error) {
      console.error('Failed to refresh runs:', error);
    }
    
    // Mark that we need to refresh parent when the run history modal closes
    needsParentRefreshRef.current = true;
  };

  const handleCloseRunsModal = () => {
    setShowRunsModal(false);
    
    // If we added/edited/deleted runs, refresh the parent to update title progress
    if (needsParentRefreshRef.current) {
      needsParentRefreshRef.current = false;
      onRunUpdated?.();
    }
  };

  const getActionMenuOptions = (run: IDogRunSummary) => {
    if (!run) return null;
    const isManualEntry = run.data_source === Constants.run_data_source.manual_entry;
    if (run.verified) return null;
    if (deletingRunIdRef.current === run._id) return null;
    if (!isManualEntry) return null;
    return [
      { label: 'Edit', onClick: () => handleEditRunRef.current(run) },
      { label: 'Delete', onClick: () => handleDeleteRunRef.current(run._id) }
    ] as IContextMenuOption[];
  };

  const ActionsCellRenderer = (params: any) => {
    const run = params.data;
    if (!run) return null;
    if (run.verified) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#10b981', fontWeight: 600 }}>Verified</span>;
    if (deletingRunIdRef.current === run._id) return <span style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>Deleting...</span>;
    const options = params.getOptions(run);
    if (!options) return null;
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}><ContextMenu options={options} disablePortal /></div>;
  };

  // AGGrid column definitions - base columns (without Status)
  const baseColumnDefs: ColDef[] = [
    {
      headerName: 'Date',
      field: 'trial_date',
      valueFormatter: (params) => params.value ? moment(params.value).format('M/D/YYYY') : '-',
      minWidth: 120,
      flex: 1,
      filter: 'agDateColumnFilter'
    },
    {
      headerName: 'Element',
      field: 'element',
      valueFormatter: (params) => params.value || '-',
      minWidth: 135,
      flex: 1,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Level',
      field: 'level',
      valueFormatter: (params) => params.value || '-',
      minWidth: 120,
      flex: 1,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Division',
      field: 'class',
      valueFormatter: (params) => params.value || '-',
      minWidth: 130,
      flex: 1,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Result',
      field: 'result_status',
      valueFormatter: (params) => params.value || '-',
      cellStyle: (params) => {
        if (params.value === Constants.run_result_status.qualified) {
          return { color: '#10b981', fontWeight: 600 };
        } else if (params.value === Constants.run_result_status.not_qualified) {
          return { color: '#ef4444', fontWeight: 600 };
        }
        return undefined;
      },
      minWidth: 120,
      flex: 1,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Event',
      field: 'event_name',
      valueFormatter: (params) => params.value || '-',
      minWidth: 200,
      flex: 2,
      filter: 'agTextColumnFilter'
    },
    {
      headerName: 'Actions',
      cellRenderer: ActionsCellRenderer,
      cellRendererParams: { getOptions: getActionMenuOptions },
      cellStyle: { overflow: 'visible', zIndex: 1 },
      minWidth: 120,
      flex: 1,
      filter: false
    }
  ];

  // Column definitions with Status (for title-specific modals)
  const columnDefsWithStatus: ColDef[] = [
    {
      headerName: 'Status',
      field: 'indicator',
      cellRenderer: (params: any) => {
        const indicator = params.value;
        
        if (indicator === 'counting') {
          return '✔️';
        } else if (indicator === 'extra') {
          return '⏳';
        } else if (indicator === 'non-qualifying') {
          return '❌';
        }
        return '';
      },
      minWidth: 120,
      maxWidth: 120,
      cellStyle: { fontSize: '16px', textAlign: 'center' },
      filter: 'agTextColumnFilter'
    },
    ...baseColumnDefs
  ];

  // Get total titles count
  const totalTitles = noseworkData.earned_titles?.length || 0;

  return (
    <div className="noseworkCard">
      <div className="cardHeader">
        <div className="headerLeft">
          <h3>UKC Nosework</h3>
          <p className="cardSubtitle">Detection & Scent Work</p>
        </div>
        <div className="headerRight">
          <div className="statsGrid">
            <div className="statItem">
              <div className="statLabel">Total Titles</div>
              <div className="statValue">{totalTitles}</div>
            </div>
            <div className="statItem">
              <div className="statLabel">Qualified Runs</div>
              <div className="statValue">{noseworkData.total_qs}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Earned Titles */}
      {noseworkData.earned_titles && noseworkData.earned_titles.length > 0 && (
        <div className="earnedTitlesSection">
          <div className="earnedTitlesHeader">
            <h4>Earned Titles ({noseworkData.earned_titles.length})</h4>
            {noseworkData.earned_titles.length > 10 && (
              <button 
                className="viewAllTitlesLink"
                onClick={() => setShowAllTitlesModal(true)}
              >
                View All
              </button>
            )}
          </div>
          <div className="titleBadges limited">
            {noseworkData.earned_titles.slice(0, 10).map(renderTitleBadge)}
          </div>
        </div>
      )}

      {/* Next Milestone */}
      {/* Removed - multiple tracks make a single milestone confusing */}

      {/* Progress Grid */}
      <div className="progressGrid">
        <div className="gridHeader">
          <div className="levelLabel">Level</div>
          <div className="elementLabel">Interior</div>
          <div className="elementLabel">Exterior</div>
          <div className="elementLabel">Container</div>
          <div className="elementLabel">Vehicle</div>
          <div className="elementLabel">Handler<br/>Disc.</div>
          <div className="levelTitleLabel">Level<br/>Title</div>
          <div className="championLabel">Class<br/>Champion</div>
          <div className="grandChampionLabel">Grand<br/>Champion</div>
        </div>
        {noseworkData.levels.map(renderLevelRow)}
      </div>

      {/* View Runs Link */}
      {/* View Run History Link - Always show, even with 0 runs */}
      {(onRunUpdated || (noseworkData.recent_runs && noseworkData.recent_runs.length > 0)) ? (
        <div className="viewRunsSection">
          <button 
            className="viewRunsLink"
            onClick={() => setShowRunsModal(true)}
          >
            {onRunUpdated ? 'View & Edit Run History' : 'View Run History'} ({noseworkData.total_trials || 0} runs)
          </button>
        </div>
      ) : null}

      {/* Runs Modal */}
      <Modal
        isShown={showRunsModal}
        hide={handleCloseRunsModal}
        className="runsHistoryModal"
        modalContent={
          <div className="runsModal">
            <h2>UKC Nosework - Run History</h2>

            <div className="runsModalContent">
              <div className="runsSection">
                <div className="runsSectionHeader">
                  <h4>ALL RUNS ({localRuns.length})</h4>
                </div>
                <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                  <AgGridReact
                    rowData={localRuns}
                    columnDefs={baseColumnDefs}
                    domLayout='normal'
                    suppressCellFocus={true}
                    theme={themeQuartz}
                    getRowId={(params) => params.data._id}
                  />
                </div>
              </div>

              <div className="modalActions">
                <Button onClick={handleAddManualRun}>
                  + Add Manual Run
                </Button>
              </div>
            </div>
          </div>
        }
      />

      {/* Manual Run Entry Modal */}
      <ManualRunEntryModal
        key={editingRun?._id || 'new-run'} // Force remount when switching between add/edit
        isOpen={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
          setEditingRun(undefined);
        }}
        dog={dog}
        sportType={Constants.show_type.nosework}
        sanctioningClub={Constants.sanctioning_club.UKC}
        onSuccess={handleModalSuccess}
        editRun={editingRun as any}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isShown={deleteConfirmModal.isShown}
        hide={deleteConfirmModal.toggle}
        modalContent={
          <div className="deleteConfirmModal">
            <h2>Delete Run</h2>
            <p>Are you sure you want to delete this run? This will recalculate your title progress.</p>
            <div className="modalActions">
              <Button onClick={deleteConfirmModal.toggle} variant="secondary">
                Cancel
              </Button>
              <Button onClick={confirmDelete} className="deleteButton">
                Delete
              </Button>
            </div>
          </div>
        }
      />

      {/* Champion Runs Modal */}
      {championModalData && (
        <Modal
          isShown={showChampionRunsModal}
          hide={() => setShowChampionRunsModal(false)}
          className="runsHistoryModal"
          modalContent={
            <div className="runsModal">
              <h2>{championModalData.titleName}</h2>
              <p style={{ marginBottom: '16px', color: '#666' }}>
                Showing {championModalData.runs.length} total run(s) - {championModalData.runs.filter(r => r.indicator === 'counting').length} counting toward this title ({championModalData.progress}/{championModalData.required} progress)
              </p>
              
              {/* Element Breakdown Summary */}
              {championModalData.elementBreakdown && (
                <div style={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(4, 1fr)', 
                  gap: '12px', 
                  marginBottom: '16px',
                  padding: '16px',
                  background: '#f9fafb',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb'
                }}>
                  {championModalData.elementBreakdown.map(el => {
                    const isComplete = el.count >= el.required;
                    const progress = `${el.count}/${el.required}`;
                    
                    return (
                      <div 
                        key={el.element}
                        style={{
                          textAlign: 'center',
                          padding: '8px',
                          borderRadius: '6px',
                          background: isComplete ? '#d1fae5' : '#fff',
                          border: isComplete ? '2px solid #10b981' : '2px solid #e5e7eb'
                        }}
                      >
                        <div style={{ 
                          fontSize: '13px', 
                          fontWeight: 600, 
                          color: '#374151',
                          marginBottom: '4px'
                        }}>
                          {el.elementName}
                        </div>
                        <div style={{ 
                          fontSize: '18px', 
                          fontWeight: 700,
                          color: isComplete ? '#059669' : '#6b7280'
                        }}>
                          {progress}
                        </div>
                        <div style={{ fontSize: '11px', color: '#6b7280' }}>
                          {isComplete ? '✓ Complete' : 'In Progress'}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
              
              <div style={{ marginBottom: '12px', fontSize: '14px', color: '#666' }}>
                <div><strong>✔️ Counting runs</strong> - Runs that count toward the title</div>
                <div><strong>⏳ Extra runs</strong> - Additional qualifying runs beyond the requirement</div>
                <div><strong>❌ Non-qualifying runs</strong> - Did not meet qualifying criteria</div>
              </div>

              <div className="runsModalContent">
                <div className="runsSection">
                  <div className="ag-theme-quartz" style={{ height: 400, width: '100%' }}>
                    <AgGridReact
                      rowData={championModalData.runs}
                      columnDefs={columnDefsWithStatus}
                      domLayout='normal'
                      suppressCellFocus={true}
                      theme={themeQuartz}
                      getRowId={(params) => params.data._id}
                    />
                  </div>
                </div>

                <div className="modalActions">
                  <Button onClick={() => setShowChampionRunsModal(false)} variant="secondary">
                    Close
                  </Button>
                </div>
              </div>
            </div>
          }
        />
      )}

      {/* All Titles Modal */}
      <Modal
        isShown={showAllTitlesModal}
        hide={() => setShowAllTitlesModal(false)}
        className="allTitlesModal"
        modalContent={
          <div className="allTitlesModalContent">
            <h2>All Earned Titles ({noseworkData.earned_titles?.length || 0})</h2>
            <div className="titleBadgesGrid">
              {noseworkData.earned_titles?.map(renderTitleBadge)}
            </div>
            <div className="modalActions">
              <Button onClick={() => setShowAllTitlesModal(false)} variant="secondary">
                Close
              </Button>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default NoseworkCard;

